import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { isHistoryExcludedPath } from '@/config/page-titles';

export interface PageVisit {
  key: string;
  url: string;
  title: string;
  clientId?: string;
  clientName?: string;
}

interface PageHistory {
  scope: string;
  keys: string[];
  index: number;
  current: PageVisit;
  recent: PageVisit[];
  pinned: PageVisit[];
  /** URL for each key in `keys`, so travel can fall back to a direct navigation. */
  urlsByKey: Record<string, string>;
}

interface HistoryOptions {
  accountId?: string | null;
  clientId?: string;
  clientName?: string;
}

export const RECENT_PAGE_LIMIT = 25;

/**
 * How long to wait for `navigate(delta)` to actually move the app before
 * navigating to the remembered URL instead.
 *
 * Pages that embed a third-party iframe (the cognitive assessment, the
 * questionnaire host) let that frame push its own entries onto the browser's
 * session history. A `history.go(-1)` then rewinds the *frame*, not the app,
 * and the back/forward arrows look dead. When the location key hasn't changed
 * by the time this elapses, the step was swallowed and we navigate by URL.
 */
const TRAVEL_FALLBACK_MS = 400;

/**
 * Carried on the location `state` of a fallback navigation so the reducer
 * below can tell it apart from an ordinary push. A plain `navigate(url)`
 * would otherwise be recorded as a brand-new visit appended to the end of
 * `keys`, which strands the entry the fallback was trying to reach (and
 * anything after it) outside the forward stack — canGoForward reports false
 * even though there is a page to go forward to.
 */
interface TravelFallbackState {
  __travelFallbackIndex?: number;
}

function readPages(storageType: 'sessionStorage' | 'localStorage', key: string): PageVisit[] {
  try {
    const parsed: unknown = JSON.parse(window[storageType].getItem(key) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (page): page is PageVisit =>
          !!page &&
          typeof page === 'object' &&
          typeof page.url === 'string' &&
          page.url.startsWith('/') &&
          !page.url.startsWith('//') &&
          !page.url.includes('\\') &&
          typeof page.title === 'string' &&
          typeof page.key === 'string' &&
          (page.clientId === undefined || typeof page.clientId === 'string') &&
          (page.clientName === undefined || typeof page.clientName === 'string')
      )
      .filter((page) => !isHistoryExcludedPath(page.url))
      .filter((page, index, pages) => pages.findIndex((p) => p.url === page.url) === index)
      .map((page) => ({ ...page, key: '' }));
  } catch {
    return [];
  }
}

function initialHistory(scope: string, current: PageVisit): PageHistory {
  const stored = scope
    ? readPages('sessionStorage', `${scope}:recent`).filter((page) => page.url !== current.url)
    : [];
  return {
    scope,
    keys: [current.key],
    index: 0,
    current,
    recent: (isHistoryExcludedPath(current.url) ? stored : [current, ...stored]).slice(
      0,
      RECENT_PAGE_LIMIT
    ),
    pinned: scope ? readPages('localStorage', `${scope}:pinned`) : [],
    urlsByKey: { [current.key]: current.url },
  };
}

export function usePageHistory(title: string, options: HistoryOptions = {}) {
  const { accountId, clientId, clientName } = options;
  const location = useLocation();
  const action = useNavigationType();
  const navigate = useNavigate();
  const { key, pathname, search, hash, state: locationState } = location;
  const url = pathname + search + hash;
  const scope = accountId
    ? `page-history:v2:${import.meta.env.VITE_API_URL || ''}:${accountId}`
    : '';
  const [history, setHistory] = useState<PageHistory>(() =>
    initialHistory(scope, { key, url, title, clientId, clientName })
  );
  const [persistenceFailed, setPersistenceFailed] = useState(false);

  useEffect(() => {
    const fallbackIndex = (locationState as TravelFallbackState | null)?.__travelFallbackIndex;
    setHistory((previousState) => {
      const visit = { key, url, title, clientId, clientName };
      const previous = previousState.scope === scope ? previousState : initialHistory(scope, visit);
      const metadataOnly = previous.current.key === key && previous.current.url === url;
      let keys = [...previous.keys];
      let index = previous.index;
      if (!metadataOnly) {
        if (
          typeof fallbackIndex === 'number' &&
          fallbackIndex >= 0 &&
          fallbackIndex < keys.length
        ) {
          // The fallback landed. Alias the fresh key onto the logical slot it
          // was aiming for instead of appending a new entry, so everything
          // after that slot stays reachable via Forward.
          keys[fallbackIndex] = key;
          index = fallbackIndex;
        } else {
          const knownIndex = keys.indexOf(key);
          if (action === 'POP') {
            if (knownIndex >= 0) index = knownIndex;
            else {
              keys = [key];
              index = 0;
            }
          } else if (action === 'REPLACE') keys[index] = key;
          else {
            keys = [...keys.slice(0, index + 1), key].slice(-100);
            index = keys.length - 1;
          }
        }
      }
      const updateDetails = (page: PageVisit) => ({
        ...page,
        ...(page.url === url ? visit : {}),
        ...(clientId && clientName && page.clientId === clientId ? { clientName } : {}),
      });
      const withoutThisUrl = previous.recent.filter((page) => page.url !== url);
      const recent = metadataOnly
        ? previous.recent.map(updateDetails)
        : isHistoryExcludedPath(url)
          ? withoutThisUrl
          : [visit, ...withoutThisUrl].slice(0, RECENT_PAGE_LIMIT);
      const urlsByKey: Record<string, string> = {};
      for (const entryKey of keys) {
        const entryUrl = entryKey === key ? url : previous.urlsByKey[entryKey];
        if (entryUrl) urlsByKey[entryKey] = entryUrl;
      }
      return {
        scope,
        keys,
        index,
        current: visit,
        recent,
        pinned: previous.pinned.map(updateDetails),
        urlsByKey,
      };
    });
  }, [scope, key, url, title, clientId, clientName, action, locationState]);

  useEffect(() => {
    if (!scope || history.scope !== scope) return;
    try {
      sessionStorage.setItem(`${scope}:recent`, JSON.stringify(history.recent));
      localStorage.setItem(`${scope}:pinned`, JSON.stringify(history.pinned));
      setPersistenceFailed(false);
    } catch {
      setPersistenceFailed(true);
    }
  }, [scope, history]);

  useEffect(() => {
    if (!scope) return;
    const syncPins = (event: StorageEvent) => {
      if (event.key !== `${scope}:pinned`) return;
      const pinned = readPages('localStorage', `${scope}:pinned`);
      setHistory((previous) => (previous.scope === scope ? { ...previous, pinned } : previous));
    };
    window.addEventListener('storage', syncPins);
    return () => window.removeEventListener('storage', syncPins);
  }, [scope]);

  // Tracks the key the router is actually on, so `travel` can tell whether a
  // history step moved the app or was swallowed by an embedded iframe.
  const latestKey = useRef(key);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    latestKey.current = key;
  }, [key]);

  useEffect(
    () => () => {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    },
    []
  );

  /**
   * Step `delta` entries through history, then verify it landed. If the app is
   * still on the same entry once `TRAVEL_FALLBACK_MS` has passed, navigate to
   * the remembered URL so the arrows always do something.
   */
  const travel = (delta: number, targetIndex: number) => {
    const fromKey = key;
    const targetUrl = history.urlsByKey[history.keys[targetIndex]];
    navigate(delta);
    if (!targetUrl || targetUrl === url) return;
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    fallbackTimer.current = setTimeout(() => {
      if (latestKey.current === fromKey) {
        const fallbackState: TravelFallbackState = { __travelFallbackIndex: targetIndex };
        navigate(targetUrl, { state: fallbackState });
      }
    }, TRAVEL_FALLBACK_MS);
  };

  const isCurrent = history.scope === scope && history.current.key === key;
  const currentPage = { key, url, title, clientId, clientName };
  const canGoBack = isCurrent && history.index > 0;
  const canGoForward = isCurrent && history.index < history.keys.length - 1;
  const pinnedPages = history.scope === scope ? history.pinned : [];

  return {
    currentPage,
    recentPages:
      history.scope === scope
        ? history.recent.filter(
            (page) => page.url !== url && !pinnedPages.some((pin) => pin.url === page.url)
          )
        : [],
    pinnedPages,
    persistenceFailed,
    canGoBack,
    canGoForward,
    goBack: () => canGoBack && travel(-1, history.index - 1),
    goForward: () => canGoForward && travel(1, history.index + 1),
    clearHistory: () =>
      setHistory((previous) => ({ ...previous, recent: [], keys: [key], index: 0 })),
    togglePin: (page: PageVisit) =>
      setHistory((previous) => ({
        ...previous,
        pinned: previous.pinned.some((pin) => pin.url === page.url)
          ? previous.pinned.filter((pin) => pin.url !== page.url)
          : [...previous.pinned, page],
      })),
    openPage: (page: PageVisit) => {
      if (page.url === url) return;
      const index = history.keys.indexOf(page.key);
      if (index >= 0) travel(index - history.index, index);
      else navigate(page.url);
    },
  };
}
